import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import PositionDetailDrawer from "@/components/PositionDetailDrawer";

export default function PositionsPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drawerSymbol, setDrawerSymbol] = useState(null);
  const [drawerScrollTarget, setDrawerScrollTarget] = useState(null);
  const [sp, setSp] = useSearchParams();

  // Deep-link: /positions?s=AAPL[&drawer=news] opens the drawer. Powers the
  // heatmap sparkline click-through — lands users on the sentiment section.
  useEffect(() => {
    const s = sp.get("s");
    if (!s) return;
    setDrawerSymbol(s.toUpperCase());
    setDrawerScrollTarget(sp.get("drawer") || null);
    // Clear the query so a subsequent reload doesn't re-open the drawer.
    const next = new URLSearchParams(sp);
    next.delete("s");
    next.delete("drawer");
    setSp(next, { replace: true });
  }, [sp, setSp]);

  const load = async () => {
    try {
      const { data } = await api.get("/portfolio");
      setPortfolio(data);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const resetAcct = async () => {
    if (!window.confirm("Reset paper trading account? All positions, trades, and bot logs will be cleared.")) return;
    setBusy(true);
    try {
      await api.post("/account/reset");
      toast.success("Account reset to $100,000");
      load();
    } catch { toast.error("Reset failed"); }
    finally { setBusy(false); }
  };

  const holdings = portfolio?.holdings || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="positions-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Positions</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2">Positions</h1>
        </div>
        <Button
          onClick={resetAcct} disabled={busy}
          data-testid="reset-account-btn"
          variant="outline"
          className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest gap-2"
        >
          <ArrowsCounterClockwise size={12} /> Reset paper account
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Portfolio value</div>
          <div className="mono text-3xl font-bold mt-2">{fmtMoney(portfolio?.total_value)}</div>
        </div>
        <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Cash</div>
          <div className="mono text-3xl font-bold mt-2">{fmtMoney(portfolio?.cash)}</div>
        </div>
        <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Invested</div>
          <div className="mono text-3xl font-bold mt-2">{fmtMoney(portfolio?.invested_value)}</div>
        </div>
        <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Unrealized P/L</div>
          <div className={`mono text-3xl font-bold mt-2 ${(portfolio?.total_unrealized_pl ?? 0) >= 0 ? "profit" : "loss"}`}>
            {fmtMoney(portfolio?.total_unrealized_pl, { showSign: true })}
          </div>
        </div>
        <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Realized P/L</div>
          <div className={`mono text-3xl font-bold mt-2 ${(portfolio?.realized_pl ?? 0) >= 0 ? "profit" : "loss"}`}>
            {fmtMoney(portfolio?.realized_pl, { showSign: true })}
          </div>
        </div>
      </div>

      <Card>
        <CardHeader title="All positions" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-2.5">Symbol</th>
                <th className="text-right px-3 py-2.5">Quantity</th>
                <th className="text-right px-3 py-2.5">Avg cost</th>
                <th className="text-right px-3 py-2.5">Current</th>
                <th className="text-right px-3 py-2.5">Market value</th>
                <th className="text-right px-3 py-2.5">Day %</th>
                <th className="text-right px-3 py-2.5">Unrealized P/L</th>
                <th className="text-right px-3 py-2.5">Realized P/L</th>
                <th className="text-right px-5 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody className="mono">
              {holdings.map((h) => {
                const realized = h.realized_pl ?? 0;
                const hasRealized = (h.num_sells ?? 0) > 0;
                return (
                <tr
                  key={h.symbol}
                  className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer"
                  data-testid={`position-full-${h.symbol}`}
                  onClick={() => setDrawerSymbol(h.symbol)}
                >
                  <td className="px-5 py-3">
                    <div className="font-semibold">{h.symbol}</div>
                    <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{h.name}</div>
                  </td>
                  <td className="text-right px-3 py-3">{h.quantity}</td>
                  <td className="text-right px-3 py-3">{fmtMoney(h.avg_cost)}</td>
                  <td className="text-right px-3 py-3">{fmtMoney(h.current_price)}</td>
                  <td className="text-right px-3 py-3">{fmtMoney(h.market_value)}</td>
                  <td className={`text-right px-3 py-3 ${h.change_pct >= 0 ? "profit" : "loss"}`}>{fmtPct(h.change_pct)}</td>
                  <td className={`text-right px-3 py-3 ${h.unrealized_pl >= 0 ? "profit" : "loss"}`}>
                    {fmtMoney(h.unrealized_pl, { showSign: true })}
                    <div className="text-[10px]">{fmtPct(h.unrealized_pl_pct)}</div>
                  </td>
                  <td
                    className={`text-right px-3 py-3 ${hasRealized ? (realized >= 0 ? "profit" : "loss") : "text-zinc-600"}`}
                    data-testid={`realized-cell-${h.symbol}`}
                  >
                    {hasRealized ? fmtMoney(realized, { showSign: true }) : "—"}
                    {hasRealized && (
                      <div className="text-[10px] text-zinc-500">
                        {h.num_sells} sell{h.num_sells === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  <td className="text-right px-5 py-3">
                    <Link
                      to={`/trade?s=${h.symbol}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805]"
                    >
                      Trade <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              );
              })}
              {holdings.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-16 text-center mono text-xs text-zinc-600">
                  No open positions.<br />
                  <Link to="/trade" className="text-white underline mt-2 inline-block">Place your first trade →</Link>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {portfolio?.closed_positions?.length > 0 && (
        <Card data-testid="closed-positions-card">
          <CardHeader
            title="Closed positions"
            action={
              <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                Realized P/L from symbols you've fully exited
              </span>
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2.5">Symbol</th>
                  <th className="text-right px-3 py-2.5">Sells</th>
                  <th className="text-right px-3 py-2.5">Realized P/L</th>
                  <th className="text-right px-5 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody className="mono">
                {portfolio.closed_positions.map((c) => (
                  <tr
                    key={c.symbol}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer"
                    data-testid={`closed-row-${c.symbol}`}
                    onClick={() => setDrawerSymbol(c.symbol)}
                  >
                    <td className="px-5 py-3 font-semibold">{c.symbol}</td>
                    <td className="text-right px-3 py-3 text-zinc-400">{c.num_sells}</td>
                    <td className={`text-right px-3 py-3 font-bold ${c.realized_pl >= 0 ? "profit" : "loss"}`}>
                      {fmtMoney(c.realized_pl, { showSign: true })}
                    </td>
                    <td className="text-right px-5 py-3">
                      <Link
                        to={`/trade?s=${c.symbol}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805]"
                      >
                        Reopen <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <PositionDetailDrawer
        symbol={drawerSymbol}
        onClose={() => { setDrawerSymbol(null); setDrawerScrollTarget(null); }}
        scrollToNews={drawerScrollTarget === "news"}
      />
    </div>
  );
}
